import { z } from 'zod'
import {
  EndDate,
  Id,
  IsActive,
  NextId,
  ObjectId,
  PrevId,
  StartDate,
  UpdateDate,
} from './common-schemas'
import { GarItem } from './gar-item'

export namespace AdmHierarchyItem {
  export interface Data {
    /** @description Unique identifier of the record. Key field */
    readonly id: number

    /** @description Global unique identifier of the object */
    readonly objectId: number

    /** @description Parent object identifier */
    readonly parentObjId?: number

    /** @description Identifier of the changing transaction */
    readonly changeId: number

    /** @description Region code */
    readonly regionCode?: string

    /** @description District code */
    readonly areaCode?: string

    /** @description City code */
    readonly cityCode?: string

    /** @description Locality code */
    readonly placeCode?: string

    /** @description Code of the planning structure element */
    readonly planCode?: string

    /** @description Street code */
    readonly streetCode?: string

    /** @description Identifier of the record linking to the previous historical record */
    readonly prevId?: number

    /** @description Identifier of the record linking to the subsequent historical record */
    readonly nextId?: number

    /** @description Date of entry (update) of the record */
    readonly updateDate: Date

    /** @description Start date of the record */
    readonly startDate: Date

    /** @description End date of the record */
    readonly endDate: Date

    /** @description Sign of an active address object */
    readonly isActive: boolean

    /** @description Materialized path to the object (full hierarchy) */
    readonly path: string
  }
}

/** @description Information about hierarchy in administrative division */
export class AdmHierarchyItem extends GarItem<
  'AdmHierarchyItem',
  AdmHierarchyItem.Data
> {
  static readonly SCHEMA: GarItem.Schema<AdmHierarchyItem.Data> = z
    .strictObject({
      ID: Id,

      OBJECTID: ObjectId,

      PARENTOBJID: z.coerce
        .number<string>()
        .int()
        .describe('Parent object identifier')
        .optional(),

      CHANGEID: z.coerce
        .number<string>()
        .int()
        .describe('Identifier of the changing transaction'),

      REGIONCODE: z.string().describe('Region code').optional(),

      AREACODE: z.string().describe('District code').optional(),

      CITYCODE: z.string().describe('City code').optional(),

      PLACECODE: z.string().describe('Locality code').optional(),

      PLANCODE: z
        .string()
        .describe('Code of the planning structure element')
        .optional(),

      STREETCODE: z.string().describe('Street code').optional(),

      PREVID: PrevId.optional(),

      NEXTID: NextId.optional(),

      UPDATEDATE: UpdateDate,

      STARTDATE: StartDate,

      ENDDATE: EndDate,

      ISACTIVE: IsActive,

      PATH: z
        .string()
        .describe('Materialized path to the object (full hierarchy)'),
    })
    .transform<AdmHierarchyItem.Data>(attributes => ({
      id: attributes.ID,
      objectId: attributes.OBJECTID,
      parentObjId: attributes.PARENTOBJID,
      changeId: attributes.CHANGEID,
      regionCode: attributes.REGIONCODE,
      areaCode: attributes.AREACODE,
      cityCode: attributes.CITYCODE,
      placeCode: attributes.PLACECODE,
      planCode: attributes.PLANCODE,
      streetCode: attributes.STREETCODE,
      prevId: attributes.PREVID,
      nextId: attributes.NEXTID,
      updateDate: attributes.UPDATEDATE,
      startDate: attributes.STARTDATE,
      endDate: attributes.ENDDATE,
      isActive: attributes.ISACTIVE,
      path: attributes.PATH,
    }))

  readonly type = 'AdmHierarchyItem'

  protected get schema() {
    return AdmHierarchyItem.SCHEMA
  }
}
